import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Form, Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { PageHeader, Loading } from '../../components';
import useComponentState from '../../utils/useComponentState';
import { validateEmail } from '../../utils/validations';
import { formatRoleName } from '../../utils/dataFormatter';
import styles from './addUsers.module.scss';
import { toast } from 'react-toastify';
import { addUser } from '../../api/user';
import { getRolesList } from '../../api/RolePermission';
import {
    INITIAL_STATE,
    SUCCESS_MESSAGE,
    TOAST_MESSAGES,
    API_ERROR_MESSAGES,
} from './addUsersConstants';

const validateAllFields = (state) => {
    const errors = {};

    const emailValidation = validateEmail(state.email);
    if (!emailValidation.isValid) {
        errors.email = emailValidation.error;
    }

    if (!state.password || state.password.trim().length === 0) {
        errors.password = 'Password is required.';
    } else if (state.password.trim().length < 8) {
        errors.password = 'Password must be at least 8 characters long.';
    }

    if (!state.mobileNumber || state.mobileNumber.trim().length === 0) {
        errors.mobileNumber = "Mobile number can't be empty.";
    } else if (!/^\+94\d+$/.test(state.mobileNumber.trim())) {
        errors.mobileNumber = 'Mobile number must start with +94.';
    } else if (state.mobileNumber.trim().length < 12) {
        errors.mobileNumber = 'Mobile number must be at least 12 characters (e.g., +94XXXXXXXXX).';
    }

    if (!state.selectedRole || state.selectedRole.trim().length === 0) {
        errors.selectedRole = 'Role selection is required.';
    }

    return { isValid: Object.keys(errors).length === 0, errors };
};

const createPayload = (state) => ({
    email: state.email.trim(),
    password: state.password,
    mobile: state.mobileNumber.trim(),
    role: state.selectedRole,
    merchantIds: null,
    subMerchantId: null,
});

const AddUsers = ({ auth }) => {
    const [state, setState] = useComponentState({
        ...INITIAL_STATE,
        roles: [],
        rolesLoading: true,
    });

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        setState({ rolesLoading: true });
        try {
            const res = await getRolesList(auth.auth.token);
            setState({ roles: Array.isArray(res) ? res : [], rolesLoading: false });
        } catch (err) {
            setState({ rolesLoading: false });
            toast.error('Failed to load roles.');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({
            ...prev,
            [name]: value,
            fieldErrors: { ...prev.fieldErrors, [name]: '' },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { isValid, errors } = validateAllFields(state);
        if (!isValid) {
            setState((prev) => ({ ...prev, fieldErrors: errors }));
            return;
        }

        setState((prev) => ({ ...prev, loading: true, error: '' }));

        try {
            const payload = createPayload(state);
            const response = await addUser(auth.auth.token, payload);

            if (response.message === SUCCESS_MESSAGE) {
                toast.success(TOAST_MESSAGES.success);
                setState(INITIAL_STATE);
            } else {
                toast.error(response.message);
                setState((prev) => ({ ...prev, loading: false }));
            }
        } catch (error) {
            const errorCode = error?.message || error.response?.data?.message;
            const errorMessage = API_ERROR_MESSAGES[errorCode] || TOAST_MESSAGES.error;
            setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
            toast.error(errorMessage);
        }
    };

    const handleCancel = () => setState(INITIAL_STATE);

    return (
        <div className={styles.root_div}>
            <PageHeader text='Add Users' />

            <Card className={styles.formCard}>
                <Card.Body>
                    <Form noValidate onSubmit={handleSubmit}>
                        <div className='mb-4'>
                            <small className='text-muted'>
                                Fields marked with <span className='text-danger'>*</span> are required
                            </small>
                        </div>

                        {/* Role & Permissions */}
                        <div className={styles.sectionContainer}>
                            <div className={styles.sectionTitle}>Role &amp; Permissions</div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    User Role <span className='text-danger'>*</span>
                                </label>
                                <div className={styles.selectWrapper}>
                                    <Form.Control
                                        as='select'
                                        name='selectedRole'
                                        className={styles.roleSelect}
                                        value={state.selectedRole}
                                        onChange={handleInputChange}
                                        isInvalid={!!state.fieldErrors?.selectedRole}
                                        disabled={state.rolesLoading}
                                    >
                                        <option value=''>
                                            {state.rolesLoading ? 'Loading roles...' : 'Select a role'}
                                        </option>
                                        {state.roles.map((role) => (
                                            <option key={role.name} value={role.name}>
                                                {formatRoleName(role.name)}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <FontAwesomeIcon icon={faChevronDown} className={styles.selectIcon} />
                                </div>
                                <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                    {state.fieldErrors?.selectedRole}
                                </Form.Control.Feedback>
                            </div>
                        </div>

                        {/* Account Details */}
                        <div className={styles.sectionContainer}>
                            <div className={styles.sectionTitle}>Account Details</div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    Email Address <span className='text-danger'>*</span>
                                </label>
                                <Form.Control
                                    type='email'
                                    name='email'
                                    value={state.email}
                                    onChange={handleInputChange}
                                    placeholder='e.g. user@example.com'
                                    isInvalid={!!state.fieldErrors?.email}
                                />
                                <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                    {state.fieldErrors?.email}
                                </Form.Control.Feedback>
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    Password <span className='text-danger'>*</span>
                                </label>
                                <Form.Control
                                    type='password'
                                    name='password'
                                    value={state.password}
                                    onChange={handleInputChange}
                                    placeholder='Minimum 8 characters'
                                    isInvalid={!!state.fieldErrors?.password}
                                />
                                <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                    {state.fieldErrors?.password}
                                </Form.Control.Feedback>
                                <div className={styles.fieldHint}>Must be at least 8 characters long.</div>
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    Mobile Number <span className='text-danger'>*</span>
                                </label>
                                <Form.Control
                                    type='tel'
                                    name='mobileNumber'
                                    value={state.mobileNumber}
                                    onChange={handleInputChange}
                                    placeholder='+94XXXXXXXXX'
                                    isInvalid={!!state.fieldErrors?.mobileNumber}
                                />
                                <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                    {state.fieldErrors?.mobileNumber}
                                </Form.Control.Feedback>
                                <div className={styles.fieldHint}>Must start with +94 followed by 9 digits.</div>
                            </div>
                        </div>

                        <div className={styles.formActions}>
                            <Button variant='secondary' type='button' onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button type='submit' variant='primary' disabled={state.loading}>
                                {state.loading ? 'Adding...' : 'Add User'}
                            </Button>
                        </div>
                    </Form>

                    {state.loading && <Loading />}
                </Card.Body>
            </Card>
        </div>
    );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(AddUsers);
